import React, {useCallback, useEffect, useState} from 'react';
import _debounce from 'lodash/debounce';
import { InputAdornment, TextField, TextFieldProps, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material';


type StyledTextFieldProps = TextFieldProps & {
    valid?: boolean;
};

export const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== 'valid',
})<StyledTextFieldProps>(({ theme, valid }) => ({
    marginBottom: theme.spacing(2),
    width: '100%',
    zIndex: 0,
    '& .MuiInputBase-input': {
        // Input text is black, not the tenant's brand/primary colour.
        color: '#000000',
        borderRadius: 0,
        /* Style to override autofill background and text color */
        '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            boxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: '#000000 !important',
            transition: 'background-color 5000s ease-in-out 0s !important',
        },
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: valid ? theme.palette.input.focus : theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.input.focus,
            color: theme.palette.primary.light,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.input.focus,
            color: theme.palette.primary.light,
        },
    },
    '& .MuiInputBase-root': {
        color: '#000000',
    },
    '& .MuiInputLabel-outlined': {
        color: '#000000',
        opacity: 0.8,
    },
}));

interface DebouncedTextInputProps {
    label?: string;
    initialValue?: string;
    onChange: (value: string) => void;
    valid?: boolean;
    autoComplete?: string;
    required?: boolean;
}

const DebouncedTextInput = (props: DebouncedTextInputProps) => {
    const [value, setValue] = useState<string>('');
    const theme = useTheme();

    const debouncedOnChange = useCallback(
        _debounce(props.onChange, 700, {leading: false, trailing: true}),
        [],
    );

    useEffect(() => {
        if (props.initialValue) {
            setValue(props.initialValue);
        }
    }, [props.initialValue]);

    useEffect(() => {
        debouncedOnChange(value);
    }, [value]);

    const showValid = props.valid && value.trim() !== "";
    return (
        <StyledTextField
            value={value}
            onChange={event => setValue(event.target.value)}
            variant={'outlined'}
            label={props.label}
            color={'primary'}
            fullWidth={false}
            size={'medium'}
            required={props.required ?? true}
            autoComplete={props.autoComplete}
            InputLabelProps={{
                style: { color: 'rgba(0, 0, 0, 0.8)' }
            }}
            InputProps={{
                endAdornment: showValid
                    ? <InputAdornment position="end"><CheckIcon sx={{ color: theme.palette.input.focus }} /></InputAdornment>
                    : null,
            }}
            valid={showValid}
        />
    );
};

export default DebouncedTextInput;
